import { BigNum, QUOTE_PRECISION_EXP } from '@velocity-exchange/sdk';
import Env from 'src/environmentVariables/EnvironmentVariables';
import formatNumber from 'src/utils/formatNumber/formatNumber';
import { delimiter } from './locale';

export const toNotional = (val: number, decimals = 2) => {
	return formatNumber(BigNum.fromPrint(val.toString(), QUOTE_PRECISION_EXP), {
		type: 'currency',
		decimals,
	});
};

export const toFixedLocaleString = (num: number, decimals: number) => {
	const rounded = Math.round(num * 10 ** decimals) / 10 ** decimals;
	const [leftSide, rightSide = ''] = rounded
		.toLocaleString(Env.locale, { minimumFractionDigits: decimals })
		.split(delimiter);
	const zerosToAdd = decimals - rightSide.length;
	const rightSidePadded =
		zerosToAdd > 0
			? `${rightSide}${Array(zerosToAdd).fill('0').join('')}`
			: rightSide;
	return `${leftSide === 'NaN' ? 0 : leftSide}${
		rightSidePadded === '' ? '' : delimiter
	}${rightSidePadded}`;
};

export const formatWithMinDecimals = (
	value: number,
	displayDecimals: number,
	minDecimals = 2
) => {
	if (value === undefined) return '0';

	const string = value.toString();

	if (string === '0' || string === 'NaN') return '0';

	if (string.includes('e')) {
		const exponent = parseInt(string.match(/e-(.*)/)?.[1] ?? '0');
		return value.toFixed(exponent + (minDecimals - 1));
	}

	const zeroesMatches = string.match(/^0.(0+)/);
	if (zeroesMatches?.length) {
		const diffInZeroes = Math.max(
			0,
			zeroesMatches[1].length + minDecimals - displayDecimals
		);
		return value.toFixed(displayDecimals + diffInZeroes);
	} else {
		return toFixedLocaleString(value, displayDecimals);
	}
};

export const prettyPrintPriceBasedOnTick = (
	price: BigNum,
	tickSizeExponent: number
) => {
	return price.toNum().toLocaleString(Env.locale, {
		minimumFractionDigits: Math.max(2, tickSizeExponent),
	});
};

export const formatLeverageLabel = (value: number | string): string => {
	const numeric = typeof value === 'string' ? parseFloat(value) : value;
	if (!numeric || numeric <= 0 || Number.isNaN(numeric)) return '1x';
	const fixed = numeric.toFixed(0);
	const trimmed = fixed.replace(/\.0+$/, '').replace(/(\.[1-9]*)0+$/, '$1');
	return `${trimmed}x`;
};
